// What a reading applied to the Test form still needs before the Test can be saved with it, part by
// part (TGCLOUD-1868, plan D3 and D4; decisions 6, 13, 14 and 15; hazard 1868-H1).
//
//   const checks = useReadingChecks({ review, form, view, readOnly });
//   <ReadingChecksContext.Provider value={checks}> … </ReadingChecksContext.Provider>
//   const checks = useReadingChecksContext();     // in a group card: null outside a review
//
// THE ARGUMENTS
//   review     the review APPLIED to the form (useLabReportReview, status 'applied'): its reading
//              (the intake or hand-off answer), the choices made per contradiction, and
//              `application.plan`, the plan it was applied by (applyProposal.planApplication)
//   form       the Test form's values now
//   view       the verification view (useVerificationView): its telemetry counters, and whether the
//              report could be shown (the unverifiable acknowledgement)
//   readOnly   nothing is confirmed or entered: a saved Test's report, or a read-only member
//
// WHAT IT GIVES
//   active                         a reading is applied and can be confirmed here
//   groups, group(key)             the reading's groups (verificationGroups.groupsOf)
//   needs(key)                     what a group still needs, one sentence each (groupNeeds). A
//                                  low-confidence value counts only once it was entered here and
//                                  compared with the reading
//   heldBefore(path)               what the Test held where the application emptied a value the
//                                  reader was not confident of (verificationGroups.heldBeforeOf)
//   confirm(key)                   ONE group confirmed now, only when it needs nothing. There is no
//                                  call that confirms more than one (useGroupConfirmations)
//   isConfirmed(key), confirmedAt(key), confirmations, count, total
//   lowOf(key), lowAt(path)        the values of a group the reader was not confident of
//   commit(path, value)            a low-confidence value entered (a select chosen, a text field
//                                  left): it is compared with the reading (readingDigest twin) and
//                                  counted once for telemetry, valueTyped(differed)
//   chooseNotStated(path), isNotStated(path)
//                                  "Not stated" chosen for an interpretation the reading holds no
//                                  tier for: the form holds no interpretation, and this is the entry
//                                  (NOT_STATED_CHOICE)
//   comparisonOf(path, value)      true "Matches what was read", false "Differs from what was
//                                  read", null nothing was read to compare it with, undefined not
//                                  entered yet or still being compared
//   listOptions                    the gene list states the card offers (decisions 13, 14)
//   openConflicts, ridingConflicts the contradictions not chosen yet; those that ride with the Test
//   warnings                       READING_WARNINGS keys the reading raises
//   blockers                       why Save waits, one sentence each (saveBlockers)
//   notOnForm                      the reading's findings no variant on the form carries
//   nameGroup(key), nameValue(path)   a group, or a low-confidence value, in words
//
// NOTHING HERE IS SENT ANYWHERE BUT THE SAVE AND THE TELEMETRY COUNTS. The comparison digests what
// was entered in the browser (WebCrypto) and compares it with the reading's digest in the answer;
// the save compares again on the server, from the signed reading.
import React from 'react';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { warningsOfReading } from 'src/components/pedigree-chart/pedigree-sidebar/sections/test-results/applyProposal';
import useGroupConfirmations from './useGroupConfirmations';
import { canonical, matchesReading } from './readingDigest';
import {
  GENE_LIST_KEY,
  OVERALL_RESULT_KEY,
  classificationKeyOf,
  findingsNotOnForm,
  groupNameOf,
  groupNeeds,
  groupSignature,
  groupsOf,
  heldBeforeOf,
  identityKeyOf,
  listOptionsFor,
  lowConfidenceOf,
  lowValueName,
  openConflicts,
  saveBlockers,
  statedCountOf
} from './verificationGroups';

export const ReadingChecksContext = React.createContext(null);

/** The reading checks of the review this component is inside, or null. */
export const useReadingChecksContext = () =>
  React.useContext(ReadingChecksContext);

const EMPTY = Object.freeze([]);
const NO_ENTRIES = Object.freeze({});
const SEP = '\u0000';
// What "Not stated" chosen for an interpretation is committed as: the form holds no interpretation
// there, so the choice is kept here, where nothing typed can spell it.
const NOT_STATED_ENTRY = `${SEP}not-stated`;

/**
 * The choice a coded low-confidence field offers for "the report does not state it" where its list
 * has no such code: an interpretation the reading holds no tier for (its readingDigest null). It is
 * saved as no interpretation (verificationGroups.NOT_STATED_FIELDS; backend verification.js
 * statesNoInterpretation).
 */
export const NOT_STATED_CHOICE = 'not-stated';

export default function useReadingChecks({
  review = null,
  form = null,
  view = null,
  readOnly = false
} = {}) {
  // What a group still needs, why Save waits, and the names of the groups and values are said in the
  // selected language (TGCLOUD-1893).
  const t = useAutoTranslation();
  const reading =
    review && review.status === 'applied' ? review.reading || null : null;
  const active = !!reading && !readOnly;
  const scopeKey = active ? review.key : null;
  const groups = React.useMemo(
    () => (active ? groupsOf(reading) : EMPTY),
    [active, reading]
  );
  const low = React.useMemo(
    () => (active ? lowConfidenceOf(reading) : EMPTY),
    [active, reading]
  );
  const lowByPath = React.useMemo(
    () => new Map(low.map((l) => [l.path, l])),
    [low]
  );
  const listOptions = active ? listOptionsFor(form, reading, t) : EMPTY;
  const plan =
    (active && review.application && review.application.plan) || null;
  const choices = (active && review.choices) || NO_ENTRIES;
  const counters = view ? view.counters : null;

  const confirmations = useGroupConfirmations({
    scopeKey,
    groups,
    signatureOf: (g) => groupSignature(g, { form, listOptions }),
    onConfirmed: () => counters?.groupConfirmed()
  });

  // What was entered at each low-confidence path, and how each entry compared with the reading.
  // Kept per review: another review starts with nothing entered.
  const [entries, setEntries] = React.useState({
    scopeKey,
    committed: NO_ENTRIES,
    results: NO_ENTRIES
  });
  const current = entries.scopeKey === scopeKey ? entries : null;

  // Whether the value a low-confidence path holds was entered here and compared with the reading:
  // only then does it count towards its group (groupNeeds). 'comparing' while the digest is worked
  // out; 'not-entered' for a value the form holds that was never entered here, or not left yet.
  // "Not stated" chosen for this path (and the form still holding nothing there).
  const notStated = (path) =>
    !!current && current.committed[path] === NOT_STATED_ENTRY;
  const comparison = (path, value) => {
    const canon = canonical(path, value);
    if (!canon && notStated(path)) return 'compared';
    if (!current || !canon || current.committed[path] !== canon) {
      return 'not-entered';
    }
    return Object.prototype.hasOwnProperty.call(
      current.results,
      `${path}${SEP}${canon}`
    )
      ? 'compared'
      : 'comparing';
  };

  const needsByKey = new Map(
    groups.map((g) => [
      g.key,
      groupNeeds({
        group: g,
        form,
        reading,
        plan,
        choices,
        listOptions,
        comparison,
        notStated,
        t
      })
    ])
  );
  const needs = (key) => needsByKey.get(key) || EMPTY;
  // What the Test held where the application emptied a value the reader was not confident of.
  const heldBefore = active ? heldBeforeOf(plan, reading) : new Map();
  // What was counted for telemetry, per path: one count for each value entered, not each blur.
  const counted = React.useRef({ scopeKey: null, values: new Map() });
  if (counted.current.scopeKey !== scopeKey) {
    counted.current = { scopeKey, values: new Map() };
  }

  const commit = (path, value) => {
    if (!active) return;
    const entry = lowByPath.get(path);
    if (!entry) return;
    const canon = canonical(path, value);
    const key = `${path}${SEP}${canon}`;
    const record = (result) =>
      setEntries((prev) => {
        const base =
          prev.scopeKey === scopeKey
            ? prev
            : { scopeKey, committed: NO_ENTRIES, results: NO_ENTRIES };
        return {
          ...base,
          committed: { ...base.committed, [path]: canon },
          results:
            result === undefined
              ? base.results
              : { ...base.results, [key]: result }
        };
      });
    const count = (result) => {
      const seen = counted.current;
      if (seen.scopeKey !== scopeKey || seen.values.get(path) === canon) return;
      seen.values.set(path, canon);
      counters?.valueTyped(result === false);
    };
    if (!canon) {
      record(undefined);
      return;
    }
    // Nothing was read to compare it with: said, and counted, at once.
    if (typeof entry.readingDigest !== 'string' || !entry.readingDigest) {
      record(null);
      count(null);
      return;
    }
    record(undefined);
    matchesReading({
      runId: reading.runId,
      path,
      value,
      readingDigest: entry.readingDigest,
      printedDigest: entry.printedDigest
    })
      // A browser that cannot digest says nothing either way, rather than a guess.
      .catch(() => null)
      .then((result) => {
        setEntries((prev) =>
          prev.scopeKey === scopeKey
            ? { ...prev, results: { ...prev.results, [key]: result } }
            : prev
        );
        count(result);
      });
  };

  // "Not stated" chosen where the list has no such code (an interpretation the reading holds no tier
  // for): the form holds no value there, and the choice is kept as this path's entry. Nothing was
  // read to compare it with, and it counts once, as a value entered.
  const chooseNotStated = (path) => {
    if (!active) return;
    const entry = lowByPath.get(path);
    if (!entry || entry.readingDigest) return;
    setEntries((prev) => {
      const base =
        prev.scopeKey === scopeKey
          ? prev
          : { scopeKey, committed: NO_ENTRIES, results: NO_ENTRIES };
      return {
        ...base,
        committed: { ...base.committed, [path]: NOT_STATED_ENTRY }
      };
    });
    const seen = counted.current;
    if (
      seen.scopeKey === scopeKey &&
      seen.values.get(path) !== NOT_STATED_ENTRY
    ) {
      seen.values.set(path, NOT_STATED_ENTRY);
      counters?.valueTyped(false);
    }
  };

  const comparisonOf = (path, value) => {
    if (!current) return undefined;
    const canon = canonical(path, value);
    if (!canon || current.committed[path] !== canon) return undefined;
    const key = `${path}${SEP}${canon}`;
    return Object.prototype.hasOwnProperty.call(current.results, key)
      ? current.results[key]
      : undefined;
  };

  const { open, riding } = active
    ? openConflicts(plan, choices)
    : { open: EMPTY, riding: EMPTY };
  // The server's, worked out from the whole reading: the proposal here may withhold the conclusion.
  const warnings = active ? warningsOfReading(reading) : EMPTY;
  const blockers = active
    ? saveBlockers({
        groups,
        isConfirmed: confirmations.isConfirmed,
        identity: reading.identity,
        acknowledged: review.acknowledged === true,
        warnings,
        acknowledgedWarnings: review.acknowledgedWarnings || new Set(),
        needsUnverifiable: !!view?.needsUnverifiableAcknowledgement,
        unverifiableAcknowledged: !!view?.unverifiableAcknowledged,
        waitingForReport: !!view?.waitingForReport,
        ridingConflicts: riding,
        t
      })
    : EMPTY;
  const group = (key) => groups.find((g) => g.key === key) || null;

  return {
    active,
    reading: active ? reading : null,
    review: active ? review : null,
    groups,
    group,
    geneListGroup: group(GENE_LIST_KEY),
    overallResultGroup: group(OVERALL_RESULT_KEY),
    identityGroupOf: (findingId) => group(identityKeyOf(findingId)),
    classificationGroupOf: (findingId) => group(classificationKeyOf(findingId)),
    needs,
    confirm: (key) => {
      if (!active || needs(key).length || confirmations.isConfirmed(key)) {
        return;
      }
      confirmations.confirm(key);
    },
    isConfirmed: confirmations.isConfirmed,
    confirmedAt: confirmations.confirmedAt,
    confirmations: confirmations.confirmations,
    count: confirmations.count,
    total: confirmations.total,
    lowOf: (key) => low.filter((l) => l.groupKey === key),
    lowAt: (path) => lowByPath.get(path) || null,
    heldBefore: (path) => (heldBefore.has(path) ? heldBefore.get(path) : null),
    commit,
    chooseNotStated,
    isNotStated: notStated,
    comparisonOf,
    listOptions,
    statedCount: active ? statedCountOf(reading) : null,
    geneList: active ? reading.geneList || null : null,
    openConflicts: open,
    ridingConflicts: riding,
    warnings,
    blockers,
    notOnForm: active ? findingsNotOnForm({ reading, form }) : EMPTY,
    nameGroup: (key) => groupNameOf(group(key), reading, t),
    nameValue: (path) => (active ? lowValueName(reading, path, t) : null)
  };
}
